import { useEffect } from 'react'

export type ComponentOutlineTarget = {
  element: HTMLElement
  componentName: string
  labelStyle?: Record<string, string>
}

type ComponentOutlineOptions = {
  enabled: boolean
  bodyAttribute: string
  labelAttribute: string
  labelStyle: Record<string, string>
  resolveTarget: (target: Element) => ComponentOutlineTarget | null
  positionLabel: (label: HTMLButtonElement, target: ComponentOutlineTarget) => void
}

export function useComponentOutline({
  enabled,
  bodyAttribute,
  labelAttribute,
  labelStyle,
  resolveTarget,
  positionLabel,
}: ComponentOutlineOptions): void {
  useEffect(() => {
    if (!enabled) {
      document.body.removeAttribute(bodyAttribute)
      return undefined
    }

    const label = document.createElement('button')
    label.type = 'button'
    label.setAttribute(labelAttribute, '')
    Object.assign(label.style, labelStyle)
    document.body.setAttribute(bodyAttribute, 'true')
    document.body.appendChild(label)

    let activeTarget: ComponentOutlineTarget | null = null
    let resetFeedbackTimer: number | undefined
    const clearFeedbackTimer = () => {
      if (resetFeedbackTimer !== undefined) {
        window.clearTimeout(resetFeedbackTimer)
        resetFeedbackTimer = undefined
      }
    }
    const showCopyFeedback = (text: string) => {
      clearFeedbackTimer()
      label.textContent = text
      resetFeedbackTimer = window.setTimeout(() => {
        if (activeTarget) label.textContent = activeTarget.componentName
      }, 1500)
    }
    const hideLabel = () => {
      activeTarget = null
      clearFeedbackTimer()
      label.style.display = 'none'
    }
    const showLabel = (target: ComponentOutlineTarget) => {
      Object.assign(label.style, target.labelStyle ?? labelStyle)
      if (
        activeTarget?.element !== target.element ||
        activeTarget.componentName !== target.componentName
      ) {
        activeTarget = target
        clearFeedbackTimer()
        label.textContent = target.componentName
      }
      positionLabel(label, target)
    }
    const copyComponentName = async () => {
      if (!activeTarget) return
      try {
        await navigator.clipboard.writeText(activeTarget.componentName)
        showCopyFeedback('已复制')
      } catch {
        showCopyFeedback('复制失败')
      }
    }
    const handlePointerMove = (event: PointerEvent) => {
      if (!(event.target instanceof Element)) {
        hideLabel()
        return
      }
      if (event.target === label) return

      const target = resolveTarget(event.target)
      if (!target) {
        hideLabel()
        return
      }
      showLabel(target)
    }
    const refreshLabelPosition = () => {
      if (activeTarget) showLabel(activeTarget)
    }

    label.addEventListener('click', () => void copyComponentName())
    document.addEventListener('pointermove', handlePointerMove, true)
    document.addEventListener('scroll', refreshLabelPosition, true)
    window.addEventListener('resize', refreshLabelPosition)

    return () => {
      document.body.removeAttribute(bodyAttribute)
      document.removeEventListener('pointermove', handlePointerMove, true)
      document.removeEventListener('scroll', refreshLabelPosition, true)
      window.removeEventListener('resize', refreshLabelPosition)
      clearFeedbackTimer()
      label.remove()
    }
  }, [bodyAttribute, enabled, labelAttribute, labelStyle, positionLabel, resolveTarget])
}
